import { FormEvent, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ReCAPTCHA from 'react-google-recaptcha';
import { ROUTES } from '../constants';
import { endpointInstance, onlyDigits } from '../methods/Methods';
import vars from '../vars';
import { Paquetes, TopUpPackage } from '../types/common';

interface LandingProps {
  phone: string;
  phoneError: string;
  onPhoneChange: (value: string) => void;
  onSubmitPhone: () => boolean;
  onPackagesLoad: (packages: TopUpPackage[]) => void;
}

const formatAmountLabel = (rawAmount: string): string => {
  const amount = Number(rawAmount);

  if (!Number.isFinite(amount)) {
    return rawAmount;
  }

  return Number.isInteger(amount) ? `${amount}` : `${amount.toFixed(2)}`;
};

const parseAmountValue = (rawAmount: string): number => {
  const amount = Number(rawAmount);
  return Number.isFinite(amount) ? amount : 0;
};

const formatDataHeadline = (dataAmount: number): string => {
  if (dataAmount <= 0) {
    return '0mb';
  }

  if (dataAmount >= 1000) {
    const gigas = dataAmount / 1000;
    const normalizedGigas = Number.isInteger(gigas) ? `${gigas}` : `${gigas.toFixed(1)}`;
    return `${normalizedGigas}gb`;
  }

  return `${dataAmount}mb`;
};

const mapPackagesFromApi = (apiPackages: Paquetes[]): TopUpPackage[] =>
  [...apiPackages]
    .sort((a, b) => parseAmountValue(a.monto) - parseAmountValue(b.monto))
    .map((item) => {
      const headline = formatDataHeadline(item.datos);
      const amountLabel = formatAmountLabel(item.monto);
      const price = parseAmountValue(item.monto);
      const receiptHeadline = headline.toUpperCase();

      return {
        id: item.Id_paquete,
        title: `Recarga ${amountLabel}`,
        headline,
        minutes: `${item.minutos} min`,
        sms: `${item.sms} sms`,
        price,
        receipt: `${receiptHeadline} Extra + ${item.minutos} MIN + ${item.sms} SMS`,
      };
    });

function Landing({ phone, phoneError, onPhoneChange, onSubmitPhone, onPackagesLoad }: LandingProps) {
  const navigate = useNavigate();
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);
  const [resultOfCaptcha, setResultOfCaptcha] = useState<string>('');
  const [apiPhoneError, setApiPhoneError] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const hasValidPhone = onlyDigits(phone).length === 10;
  const hasValidCaptcha = Boolean(resultOfCaptcha);
  const currentPhoneError = phoneError || apiPhoneError;
  const call = endpointInstance;

  const clearCaptcha = () => {
    setResultOfCaptcha('');
    endpointInstance.setToken('');
    recaptchaRef.current?.reset();
  };

  const onFinish = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!hasValidPhone || !hasValidCaptcha || isSubmitting) {
      return;
    }

    if (onSubmitPhone()) {
      setIsSubmitting(true);
      try {
        const response = await call.getRecharges(onlyDigits(phone));
        const rawPayload = response.data?.data ?? response.data;
        const payload = Array.isArray(rawPayload) ? rawPayload[0] : rawPayload;

        if (payload?.recargar === 1) {
          const loadedPackages = mapPackagesFromApi(payload?.paquetes ?? []);

          if (!loadedPackages.length) {
            setApiPhoneError('No se encontraron paquetes disponibles para la línea.');
            return;
          }

          onPackagesLoad(loadedPackages);
          setApiPhoneError('');
          navigate(ROUTES.RECHARGES);
          return;
        }

        setApiPhoneError(payload?.mensaje || 'No fue posible validar la línea.');
      } catch (error: any) {
        const statusCode = error?.response?.status;

        if (statusCode === 401) {
          clearCaptcha();
          setApiPhoneError('La validación de seguridad expiró. Marca nuevamente "No soy un robot".');
        } else {
          setApiPhoneError('No fue posible validar la línea. Intenta nuevamente.');
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const onCaptchaChange = (value: string | null) => {
    const token = value ?? '';
    setResultOfCaptcha(token);
    endpointInstance.setToken(token);
  };

  const onPhoneInputChange = (value: string) => {
    if (resultOfCaptcha) {
      clearCaptcha();
    }

    if (apiPhoneError) {
      setApiPhoneError('');
    }
    onPhoneChange(value);
  };

  return (
    <>
      <h2 className="step-title">
        <span>1</span> Recarga tu línea
      </h2>

      <form className="form-top-up d-flex flex-column mt-5" onSubmit={onFinish}>
        <div className="field-with-alert">
          <input
            type="tel"
            inputMode="numeric"
            autoComplete="tel"
            placeholder="Ingresa tu número Mega Móvil*"
            value={phone}
            onChange={(event) => onPhoneInputChange(event.target.value)}
          />
          {currentPhoneError ? (
            <p className="alert alert-danger alert-compact mb-0 text-center" role="alert">
              {currentPhoneError}
            </p>
          ) : null}
        </div>
        <div className="d-flex justify-content-center">
          <ReCAPTCHA
            ref={recaptchaRef}
            sitekey={vars.sitekey}
            onChange={(value) => onCaptchaChange(value)}
            onExpired={clearCaptcha}
            theme="light"
            style={{ margin: '0.5rem auto' }}
          />
        </div>
        <button type="submit" disabled={!hasValidPhone || !hasValidCaptcha || isSubmitting}>
          {/* Continuar */}
          {isSubmitting ? 'Consultando...' : 'Continuar'}
          {/* Opcion de loading en boton:
              reemplazar "Continuar" por:
              {isSubmitting ? 'Consultando...' : 'Continuar'}
          */}
        </button>
      </form>

      <div className="phone-info text-center mt-5">
        <p>*El número ingresado debe contener 10 dígitos</p>
      </div>
    </>
  );
}

export default Landing;
